import { useCallback, useState } from 'react';
import usePersistedPageSize from './usePersistedPageSize';

/**
 * Sunucu tarafinda sayfalanan listeler icin sayfalama + siralama yonetimi.
 *
 * NEDEN: Her listede elle yazilinca hep ayni hatalar tekrarlandi:
 *  - AntD Table'in onChange'i HEM sayfalama HEM siralama icin tetiklenir;
 *    pagination.onChange ayrica taniml kalirsa sayfa degisiminde IKI istek gider.
 *  - Siralama degisince sayfada kalinirsa kullanici bambaska bir siralamanin
 *    3. sayfasina bakiyor olur.
 *  - dataIndex'i olmayan kolonlarda (hesaplanan "Ad Soyad" gibi) AntD
 *    sorter.field'i bos birakir; columnKey'e bakmak gerekir.
 *
 * PARAMETRE ADLARI backend'deki App\Support\SortableQuery ile ayni olmali:
 * sort_by + sort_direction. (Bir donem sort_dir yazilmisti, o kullanimlar
 * buraya cevrildi - iki isim birden dolasirsa yeni yazilan kod sessizce
 * siralamayan bir liste ureti.)
 *
 * Daha eski `useServerSort` hook'u yalnizca sorter cevrimini yapar, sayfalamayi
 * yonetmez; birkac sayfa hala onu kullaniyor. YENI liste yazarken bunu kullan.
 *
 * KULLANIM:
 *   const table = useServerTable('students', 20);
 *   const fetchRows = useCallback(async (o = {}) => {
 *     const { data } = await getStudents({ ...table.params(o), search });
 *     setRows(data.data); setTotal(data.total);
 *   }, [table.page, table.pageSize, table.sorter, search]);
 *
 *   <Table
 *     columns={columns.map(c => ({ ...c, ...table.sortProps(c.key || c.dataIndex) }))}
 *     {...table.tableProps(total, fetchRows)}
 *   />
 */
const useServerTable = (storageKey, defaultPageSize = 20, defaultSorter = { field: null, order: null }) => {
  const [pageSize, setPageSize] = usePersistedPageSize(storageKey, defaultPageSize);
  const [page, setPage] = useState(1);
  const [sorter, setSorter] = useState(defaultSorter);

  /** Istege eklenecek sayfalama + siralama parametreleri. */
  const params = useCallback((override = {}) => {
    const s = override.sorter ?? sorter;
    return {
      page: override.page ?? page,
      per_page: override.pageSize ?? pageSize,
      sort_by: s?.field || undefined,
      sort_direction: s?.order === 'descend' ? 'desc' : (s?.order === 'ascend' ? 'asc' : undefined),
    };
  }, [page, pageSize, sorter]);

  /**
   * Kolona yayilacak siralama ozellikleri.
   * sorter: true = siralamayi SUNUCU yapiyor. Burada istemci siralayicisi
   * (a,b)=>... KULLANMA: yalnizca gorunen sayfayi sirar, kullaniciyi yaniltir.
   */
  const sortProps = useCallback((field) => {
    if (!field) return {};
    const key = Array.isArray(field) ? field.join('.') : field;
    return { sorter: true, sortOrder: sorter.field === key ? sorter.order : null };
  }, [sorter]);

  /**
   * Table'a yayilacak props.
   *
   * onChange YALNIZCA state gunceller, veriyi KENDISI cekmez. Cekme isi
   * cagiranin useEffect'ine birakilir; aksi halde hem burada hem effect'te
   * istek atilir ve her siralamada SUNUCUYA IKI ISTEK gider. Bu yuzden
   * cagiran fonksiyonun bagimlilik dizisinde page/pageSize/sorter OLMALI:
   *
   *   const fetchRows = useCallback(..., [table.page, table.pageSize, table.sorter]);
   *   useEffect(() => { fetchRows(); }, [fetchRows]);
   *
   * pagination.onChange da BILEREK verilmiyor - Table.onChange zaten
   * sayfalamayi yakaliyor.
   *
   * @param {number} total  sunucudan gelen toplam kayit sayisi
   */
  const tableProps = useCallback((total, extraPagination = {}) => ({
    onChange: (pag, _filters, nextSorter) => {
      // dataIndex'siz kolonlarda field bos gelir, columnKey dolu olur.
      const rawField = nextSorter?.order ? (nextSorter.columnKey || nextSorter.field) : null;
      const next = rawField
        ? { field: Array.isArray(rawField) ? rawField.join('.') : rawField, order: nextSorter.order }
        : { field: null, order: null };

      const sortChanged = next.field !== sorter.field || next.order !== sorter.order;

      setSorter(next);
      setPageSize(pag.pageSize);
      // Siralama degisince ilk sayfaya don - aksi halde kullanici bambaska
      // bir siralamanin 3. sayfasina bakiyor olur.
      setPage(sortChanged ? 1 : pag.current);
    },
    pagination: {
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: ['20', '50', '100'],
      ...extraPagination,
    },
  }), [page, pageSize, sorter, setPageSize]);

  /** Filtre/arama degisince ilk sayfaya donmek icin. */
  const resetToFirstPage = useCallback(() => setPage(1), []);

  return { page, pageSize, sorter, params, sortProps, tableProps, setPage, resetToFirstPage };
};

export default useServerTable;
